import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Columns3, Rows3 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { plans } from "@/components/offsite-demo/data";
import { money } from "@/components/offsite-demo/shared";
import PlanCard from "@/components/offsite-demo/PlanCard";
import Reveal from "@/components/offsite-demo/Reveal";
import DemoMenu from "@/components/offsite-demo/DemoMenu";
import { useOffsite } from "./OffsiteLayout";

export default function OffsitePlans() {
  const { brief, reset } = useOffsite();
  const navigate = useNavigate();
  const [compare, setCompare] = useState(false);
  const placed = brief.cities.filter((c) => c.name && c.count > 0);

  useEffect(() => {
    document.title = "your three days | qwip offsites";
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 w-full border-b border-border bg-background/90 backdrop-blur">
        <div className="container mx-auto px-4 laptop:px-6 desktop:px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/offsite-demo")} className="flex items-baseline gap-2">
            <span className="font-funnel font-bold text-2xl text-foreground">qwip</span>
            <span className="text-sm text-muted-foreground">offsites</span>
          </button>
          <button
            onClick={() => navigate("/offsite-demo")}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft size={15} /> change the brief
          </button>
        </div>
      </header>

      <section className="container mx-auto px-4 laptop:px-6 desktop:px-6 py-16 tablet:py-20">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-5">
            your plans
          </p>
          <h1 className="font-funnel font-bold text-3xl tablet:text-4xl laptop:text-5xl desktop:text-5xl text-foreground mb-5 max-w-3xl">
            three complete days for{" "}
            <span className="font-cormorant italic font-normal">{brief.teamSize} people.</span>
          </h1>
          <p className="text-muted-foreground max-w-2xl">
            {placed.length ? placed.map((c) => `${c.name} (${c.count})`).join(", ") : "your team"} ·{" "}
            {brief.format === "day" ? "one day" : "one night away"} ·{" "}
            {brief.dateValue || "flexible dates"} · {money(brief.budgetTotal)} budget
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <button
              onClick={() => setCompare((c) => !c)}
              className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:border-primary/60 transition-colors"
            >
              {compare ? <Rows3 size={14} /> : <Columns3 size={14} />}
              {compare ? "back to the cards" : "compare all three"}
            </button>
            <p className="text-sm text-muted-foreground">
              pick one to see the whole day. illustrative estimates, not live availability.
            </p>
          </div>
        </Reveal>
      </section>

      <section className="container mx-auto px-4 laptop:px-6 desktop:px-6 pb-24">
        {compare ? (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid mobile:grid-cols-1 tablet:grid-cols-1 laptop:grid-cols-3 desktop:grid-cols-3 gap-6"
          >
            {plans.map((p) => (
              <div key={p.id} className="rounded-2xl border border-border bg-card p-7 flex flex-col">
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">
                  {p.location}
                </p>
                <h2 className="font-funnel font-bold text-xl text-foreground mb-5">{p.name}</h2>
                <dl className="space-y-3 text-sm flex-1">
                  {(
                    [
                      ["total", money(p.total)],
                      ["per person", money(Math.round(p.total / Math.max(1, brief.teamSize)))],
                      ["travel", p.travelLabel],
                      ["balance", `${p.balance} / ${100 - p.balance}`],
                      ["best for", p.bestFor],
                      ["tradeoff", p.tradeoff],
                      ["evening", p.evening.name],
                    ] as [string, string][]
                  ).map(([k, v]) => (
                    <div key={k} className="flex gap-4 justify-between">
                      <dt className="text-muted-foreground shrink-0">{k}</dt>
                      <dd className="text-foreground text-right">{v}</dd>
                    </div>
                  ))}
                </dl>
                <button
                  onClick={() => navigate(`/offsite-demo/plans/${p.id}`)}
                  className="mt-7 w-full rounded-full bg-primary text-primary-foreground py-2.5 text-sm hover:bg-primary/90 transition-colors"
                >
                  open this plan
                </button>
              </div>
            ))}
          </motion.div>
        ) : (
          <div className="grid mobile:grid-cols-1 tablet:grid-cols-2 laptop:grid-cols-3 desktop:grid-cols-3 gap-6">
            {plans.map((p, i) => (
              <PlanCard
                key={p.id}
                plan={p}
                brief={brief}
                index={i}
                onOpen={() => navigate(`/offsite-demo/plans/${p.id}`)}
              />
            ))}
          </div>
        )}
      </section>

      <DemoMenu
        onGo={() => navigate("/offsite-demo")}
        onReset={() => {
          reset();
          navigate("/offsite-demo");
        }}
      />
    </div>
  );
}
